// This is a fix for the First Post badge issue
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

/**
 * Fix the First Post badge issue by ensuring it exists and recreating it if needed
 */
export async function fixFirstPostBadge(userId: string): Promise<boolean> {
  console.log('Starting First Post badge fix...');
  
  try {
    // 1. Ensure the First Post badge exists (create it if it doesn't)
    const { data: badgeId, error: ensureBadgeError } = await supabase.rpc(
      'ensure_badge_exists',
      {
        badge_name: 'First Post',
        badge_description: 'Awarded for creating your first post',
        badge_icon: '📝',
        badge_color: 'bg-blue-500'
      }
    );
    
    if (ensureBadgeError) {
      console.error('Error ensuring First Post badge exists:', ensureBadgeError);
      toast({
        title: "Badge Creation Failed",
        description: "Could not ensure the First Post badge exists",
        variant: "destructive",
      });
      return false;
    }
    
    console.log('First Post badge ID:', badgeId);
    
    // 2. Check if the user already has this badge
    const { data: hasBadge, error: checkBadgeError } = await supabase.rpc(
      'user_has_badge',
      {
        user_id_param: userId,
        badge_id_param: badgeId
      }
    );
    
    if (checkBadgeError) {
      console.error('Error checking if user has First Post badge:', checkBadgeError);
      toast({
        title: "Error Checking Badge Status",
        description: "Could not check if you already have the First Post badge",
        variant: "destructive",
      });
      return false;
    }
    
    // 3. Award the badge if user doesn't have it
    if (!hasBadge) {
      console.log(`Awarding First Post badge to user ${userId}...`);
      
      const { data: awarded, error: awardError } = await supabase.rpc(
        'award_badge_to_user',
        {
          user_id_param: userId,
          badge_id_param: badgeId,
          progress_current_param: 100,
          progress_total_param: 100
        }
      );
      
      if (awardError) {
        console.error('Error awarding First Post badge:', awardError);
        toast({
          title: "Badge Award Failed",
          description: "Could not award you the First Post badge",
          variant: "destructive",
        });
        return false;
      }
      
      if (awarded) {
        console.log(`Successfully awarded First Post badge to user ${userId}`);
        toast({
          title: "🎉 Badge Awarded!",
          description: "You've earned the First Post badge!",
          variant: "default",
        });
      } else {
        console.log(`Badge award operation completed but badge was not awarded`);
      }
    } else {
      console.log(`User ${userId} already has the First Post badge`);
    }
    
    return true;
  } catch (error) {
    console.error('Unexpected error fixing First Post badge:', error);
    toast({
      title: "Badge Fix Error",
      description: "An unexpected error occurred while fixing the First Post badge",
      variant: "destructive",
    });
    return false;
  }
} 